import { fetchRoundParticipants } from '../apis/competition';

import EventBusInstance from '@core/eventbus';
import type { Enum } from '@shared-types/enum.types';
import type { FavouriteClubUpdateEvent } from '@shared-types/idp.types';
import { UserEvents as UserEventType } from '@shared-types/idp.types';
import type { Player, Round, Team } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import User from '@user/User';
import { getCache, setCache } from '@utilities/cache';
import { isEqual, uniqBy } from '@utilities/collection-utils';
import { Warn } from '@utilities/logging';
import { competitionId } from '@utilities/WindowContext';

export const mapCompetitionCodeToId = {
  ucl: 1,
  uel: 14,
  women: 18,
  men: 3,
} as const;

export type PreferenceCompetitions = keyof typeof mapCompetitionCodeToId;
const syncEvents = [
  UserEventType.LOGIN,
  UserEventType.USERCHANGE,
  UserEventType.FAV_CLUB_UPDATED,
  UserEventType.FAV_TEAM_UPDATED,
  UserEventType.FOLLOWED_CLUB_UPDATED,
] as const;

export const FavSource = {
  YOUR_TEAMS: 'your teams',
  DRAW: 'draw',
  DIALOG: 'dialog',
} as const;
export type FavSource = Enum<typeof FavSource>;

const followKey = window.isClub ? 'clubs' : 'teams';

export class PreferenceService {
  pageCompetition: PreferenceCompetitions;

  constructor() {
    this.pageCompetition = Object.keys(mapCompetitionCodeToId).find(
      (key) => `${mapCompetitionCodeToId[key]}` === window.competitionId,
    ) as PreferenceCompetitions;

    if (!this.pageCompetition && !window.isClub) {
      this.pageCompetition = 'men';
    }

    const favTeam = this.retrievePreferredTeam();
    if (favTeam) {
      GoogleTagManagerHelper.addPageMeta({
        team_name: favTeam.internationalName,
      });
    }

    EventBusInstance.on('userLogged', (e: (typeof syncEvents)[number]) => this.syncPreferredTeam(e), this);
    EventBusInstance.on('userLogged', (e: (typeof syncEvents)[number]) => this.syncFollowingTeams(e), this);
    EventBusInstance.on(
      'userChangedPreferredTeam',
      (club: FavouriteClubUpdateEvent) => {
        this.syncPreferredTeam(UserEventType.FAV_CLUB_UPDATED, club.value ?? club.teamId, club.competition);
      },
      this,
    );

    EventBusInstance.on(
      'userChangedFollowedTeam',
      (e) => {
        this.syncFollowingTeams(
          UserEventType.FOLLOWED_CLUB_UPDATED,
          window.isClub
            ? e.user.data?.favourites?.clubsFollowed.split(',')
            : e.user.data?.favourites?.nationalTeamsFollowed.split(','),
        );
      },
      this,
    );

    EventBusInstance.dispatch('followingTeamsChanged', this._getLocalFollowing(followKey));

    const savedFavouriteTeam = this._getLocalFavourite(this.pageCompetition);
    window.savedFavouriteTeam = savedFavouriteTeam;
    savedFavouriteTeam && EventBusInstance.dispatch('preferredTeamChanged', savedFavouriteTeam);
  }

  get preferences() {
    return getCache<Record<string, any>>('userPreferences') ?? {};
  }

  set preferences(prefs: Record<string, any>) {
    setCache('userPreferences', prefs ?? {});
  }

  _setArrayField<T>(key: string, value: T[]) {
    const prefs = this.preferences;
    prefs[key] = value;
    this.preferences = prefs;
  }

  _getArrayField<T>(key: string): T[] {
    const value = this.preferences[key];
    if (Array.isArray(value)) {
      return value as T[];
    }
    return [];
  }

  _setFieldByCompetition<T>(key: string, value: T, competition: PreferenceCompetitions) {
    const prefs = this.preferences;

    prefs[competition] = {
      ...prefs[competition],
      [key]: value,
    };

    this.preferences = prefs;
  }

  _getFieldByCompetition<T>(key: string, competition: PreferenceCompetitions): T {
    return this.preferences[competition]?.[key] as T;
  }

  /* -----------
  PREFERRED TEAM
  ----------- */

  // when we store a team, we make sure that it's props are are their defaults
  async storePreferredTeam(club: Team, source: FavSource) {
    const currentTeam = this._getLocalFavourite(this.pageCompetition);
    const result = await this._syncLocalToGigya(club, this.pageCompetition);
    if (!result) return false;

    this._setLocalFavourite(club, this.pageCompetition);
    this.trackUserPreferences(
      source,
      club ? 'Select favorite' : 'Remove favorite',
      club ? 'select_favorite' : 'remove_favorite',
      club ?? currentTeam,
    );

    if (club) {
      this.storeWelcomeDismissalStatus(false);
    }

    return true;
  }

  async addFollowingTeam(club: Team, source: FavSource) {
    if (User.isLogged()) {
      const result = await this._addGigyaFollowing(club);
      if (!result) return false;
    }

    this.trackUserPreferences(source, 'Select followed', 'select_followed', club);

    this._addLocalFollowing(club);

    return true;
  }

  async removeFollowingTeam(club: Team, source: FavSource) {
    if (User.isLogged()) {
      const result = await this._removeGigyaFollowing(club);
      if (!result) return false;
    }

    this.trackUserPreferences(source, 'Remove followed', 'remove_followed', club);

    this._removeLocalFollowing(club);

    return true;
  }

  retrievePreferredTeam() {
    return this._getLocalFavourite(this.pageCompetition);
  }

  retrieveFollowedTeams() {
    return this._getLocalFollowing(followKey);
  }

  // ref: https://uefa.atlassian.net/wiki/spaces/IDP/pages/4024140788/Apps+Sync+Implementation
  async syncPreferredTeam(event: (typeof syncEvents)[number], clubId?: string, competition = this.pageCompetition) {
    if (!syncEvents.includes(event) || (event !== UserEventType.LOGIN && !User.isLogged())) return;

    const localFavourite = this._getLocalFavourite(competition);
    const gigyaClubId = [UserEventType.LOGIN, UserEventType.USERCHANGE].includes(event)
      ? await this._getGigyaFavourite(competition)
      : clubId;

    // Users logs in
    if (event === UserEventType.LOGIN && localFavourite && (!gigyaClubId || this._getSyncExpired(competition))) {
      // Local Team, No teams on Gigya or Not already synced: local wins
      await this._syncLocalToGigya(localFavourite, competition);
      return;
    }

    // Gigya wins
    await this._syncGigyaToLocal(gigyaClubId, localFavourite, competition);
  }

  async syncFollowingTeams(event: (typeof syncEvents)[number], clubIds?: string[]) {
    if (!syncEvents.includes(event) || (event !== UserEventType.LOGIN && !User.isLogged())) {
      return;
    }

    const gigyaClubIds = [UserEventType.LOGIN, UserEventType.USERCHANGE].includes(event)
      ? await this._getGigyaFollowingTeams()
      : clubIds;
    const localFollowing = this._getLocalFollowing(followKey);

    // Users logs in
    if (
      event === UserEventType.LOGIN &&
      localFollowing?.length &&
      (!gigyaClubIds?.length || this._getSyncExpiredByKey(followKey))
    ) {
      // Local Team, No teams on Gigya or Not already synced: local wins
      await this._syncFollowingLocalToGigya(localFollowing, gigyaClubIds);
      return;
    }

    // Gigya wins
    await this._syncFollowingGigyaToLocal(localFollowing, gigyaClubIds);
  }

  _setGigyaFavourite(team: Team, competition: PreferenceCompetitions) {
    if (competition === 'women') return User.setFavouriteWomenTeam(team?.teamCode);
    return window.isClub ? User.setFavouriteClub(competition, team?.id) : User.setFavouriteTeam(team?.teamCode);
  }

  _addGigyaFollowing(newTeam?: Team) {
    if (!newTeam) return;
    return window.isClub ? User.setFollowedClub(newTeam.id) : User.setFollowedTeam(newTeam.id);
  }

  _removeGigyaFollowing(team: Team) {
    if (!team) return;
    return window.isClub ? User.removeFollowedClub(team.id) : User.removeFollowedTeam(team.teamCode);
  }

  _getGigyaFavourite(competition: PreferenceCompetitions) {
    if (competition === 'women') return User.getFavouriteWomenTeam();
    return window.isClub ? User.getFavouriteClubId(competition) : User.getFavouriteTeamCode();
  }

  _getGigyaFollowingTeams() {
    return window.isClub ? User.getFollowedClubIds() : User.getFollowedTeamIds();
  }

  async _syncLocalToGigya(favourite: Team, competition: PreferenceCompetitions) {
    if (!User.isLogged()) return true;

    try {
      const result = await this._setGigyaFavourite(favourite, competition);
      if (result && result.status !== 'OK') {
        Warn('Error saving preferred team', result.errorMessage);
        return false;
      }
    } catch (error) {
      Warn('Error saving preferred team', error);
      return false;
    }

    this._setGigyaSynced(competition);
    return true;
  }

  async _syncFollowingLocalToGigya(localFollowing: Team[], gigyaClubIds: string[]) {
    if (!User.isLogged()) return true;

    const localFollowingIds = localFollowing?.map((t) => t.id) ?? [];
    const localFollowingCodes = localFollowing?.map((t) => t.teamCode) ?? [];

    // nothing to do
    if (isEqual(gigyaClubIds, localFollowingIds) || isEqual(gigyaClubIds, localFollowingCodes)) {
      this._setGigyaSyncedByKey(followKey);
      return;
    }

    try {
      const teamsToAdd = localFollowing.filter(
        (t) => !gigyaClubIds.includes(t.id) && !gigyaClubIds.includes(t.teamCode),
      );
      for (const team of teamsToAdd) {
        await this._addGigyaFollowing(team);
      }

      const teamsToRemove = gigyaClubIds.filter(
        (id) => !localFollowingIds.includes(id) && !localFollowingCodes.includes(id),
      );
      for (const teamId of teamsToRemove) {
        await this._removeGigyaFollowing({ id: teamId });
      }

      this._setGigyaSyncedByKey(followKey);
      return true;
    } catch (error) {
      Warn('Error saving following teams', error);
      return false;
    }
  }

  async _syncGigyaToLocal(gigyaClubId: string, localFavourite: Team, competition: PreferenceCompetitions) {
    // nothing to do
    if (localFavourite && (gigyaClubId === localFavourite.id || gigyaClubId === localFavourite.teamCode)) {
      this._setGigyaSynced(competition);
      return;
    }

    // reset local favourite
    if (!gigyaClubId) {
      this._setLocalFavourite(null, competition, true);
      return;
    }

    const roundParticipants = await fetchRoundParticipants({
      competitionId: `${mapCompetitionCodeToId[competition] ?? competitionId}`,
      seasonYear: 'current',
      teamId: window.isClub ? gigyaClubId : null,
    });
    const club = roundParticipants
      ?.find((rp) => rp.teams?.some((t) => t.id === gigyaClubId || t.teamCode === gigyaClubId))
      ?.teams?.find((t) => t.id === gigyaClubId || t.teamCode === gigyaClubId);

    if (!club) return;

    const newFavourite = {
      id: club.id,
      internationalName: club.internationalName,
      teamCode: club.teamCode,
    };

    this._setLocalFavourite(newFavourite, competition, true);
  }

  async _syncFollowingGigyaToLocal(localFollowing: Team[], gigyaClubIds: string[]) {
    const localFollowingIds = localFollowing?.map((t) => t.id) ?? [];
    const localFollowingCodes = localFollowing?.map((t) => t.teamCode) ?? [];

    // nothing to do
    if (isEqual(gigyaClubIds, localFollowingIds) || isEqual(gigyaClubIds, localFollowingCodes)) {
      this._setGigyaSyncedByKey(followKey);
      return;
    }

    // reset local following
    if (!gigyaClubIds?.length) {
      this._updateFollowingList([], true);
      return;
    }

    const extractTeams = (roundParticipants: { teams: Team[]; round: Round }[]) =>
      roundParticipants
        ?.flatMap((rp) => rp.teams)
        ?.filter((team) => gigyaClubIds.includes(team.id) || gigyaClubIds.includes(team.teamCode)) ?? [];

    let allClubs: Team[] = [];

    if (window.isClub) {
      for (const clubId of gigyaClubIds) {
        const roundParticipants = await fetchRoundParticipants({
          competitionId,
          seasonYear: 'current',
          teamId: clubId,
        });
        const clubs = extractTeams(roundParticipants).filter((team) => team.id === clubId || team.teamCode === clubId);
        allClubs.push(...clubs);
      }
    } else {
      const roundParticipants = await fetchRoundParticipants({
        competitionId,
        seasonYear: 'current',
      });
      allClubs = extractTeams(roundParticipants);
    }

    // Remove duplicates
    const newFollowing = uniqBy(uniqBy(allClubs, 'id'), 'teamCode').map((club: Team) => ({
      id: club.id,
      internationalName: club.internationalName,
      teamCode: club.teamCode,
    }));

    this._updateFollowingList(newFollowing, true);
  }

  _setGigyaSynced(competition: PreferenceCompetitions) {
    const value = new Date().getTime();
    localStorage.setItem(`gigyaSynced_${competition}`, `${value}`);
  }

  _setGigyaSyncedByKey(key: string) {
    const value = new Date().getTime();
    localStorage.setItem(`gigyaSynced_${key}`, `${value}`);
  }

  gigyaSyncExpire = 10; // in minutes
  _getSyncExpired(competition: PreferenceCompetitions) {
    const gigyaSynced = +localStorage.getItem(`gigyaSynced_${competition}`);
    return !gigyaSynced || new Date().getTime() - gigyaSynced > this.gigyaSyncExpire * 60 * 1000;
  }

  _getSyncExpiredByKey(key: string) {
    const gigyaSynced = +localStorage.getItem(`gigyaSynced_${key}`);
    return !gigyaSynced || new Date().getTime() - gigyaSynced > this.gigyaSyncExpire * 60 * 1000;
  }

  _getLocalFavourite(competition: PreferenceCompetitions) {
    return this._getFieldByCompetition<Team>('favouriteTeam', competition);
  }

  _getLocalFollowing(key: string) {
    return this._getArrayField<Team>(`following_${key}`);
  }

  _setLocalFavourite(value: Team, competition: PreferenceCompetitions, isSync = false) {
    window.savedFavouriteTeam = value;
    this._setFieldByCompetition('favouriteTeam', value, competition);
    EventBusInstance.dispatch('preferredTeamChanged', value);

    if (!isSync) return;
    this._setGigyaSynced(competition);
    window.syncFavouritesNotification && EventBusInstance.dispatch('preferredTeamSynced');
  }

  _addLocalFollowing(newTeam?: Team) {
    if (!newTeam) return;

    const localFollowing = this._getLocalFollowing(followKey);
    const updatedFollowing = [...localFollowing, newTeam];

    this._updateFollowingList(updatedFollowing);
  }

  _removeLocalFollowing(teamToRemove?: Team) {
    if (!teamToRemove) return;

    const localFollowing = this._getLocalFollowing(followKey);
    const updatedFollowing = localFollowing.filter(
      (t) => t.id !== teamToRemove.id && t.teamCode !== teamToRemove.teamCode,
    );

    this._updateFollowingList(updatedFollowing);
  }

  _updateFollowingList(updatedFollowing: Team[], isSync = false) {
    this._setArrayField<Team>(`following_${followKey}`, updatedFollowing);

    EventBusInstance.dispatch('followingTeamsChanged', updatedFollowing);

    if (!isSync) return;

    this._setGigyaSyncedByKey(followKey);
    if (window.syncFollowedNotification) {
      EventBusInstance.dispatch('followingTeamsSynced');
    }
  }
  /* --------------
  WELCOME DISMISSAL
  -------------- */

  storeWelcomeDismissalStatus(dismissedWelcome: boolean) {
    this._setFieldByCompetition('dismissedWelcome', dismissedWelcome, this.pageCompetition);
  }

  retrieveWelcomeDismissalStatus() {
    return !!this._getFieldByCompetition('dismissedWelcome', this.pageCompetition);
  }

  /* --------------
  FOLLOW PLAYER
  -------------- */

  storeFollowPlayer(player: Player) {
    this._setFieldByCompetition('followPlayer', player, this.pageCompetition);
  }

  retrieveFollowPlayer() {
    return this._getFieldByCompetition('followPlayer', this.pageCompetition);
  }

  /* ------------
  GENERAL PURPOSE
  ------------ */

  trackUserPreferences(source: FavSource, eventAction: string, eventName: string, team: Team | null) {
    const teamValue = team?.internationalName;
    const eventData: Record<string, string> = {
      team_name: teamValue,
      section_reference: source,
    };
    const action = [eventAction, teamValue].filter(Boolean).join(' - ');

    GoogleTagManagerHelper.event('Click', action, source, eventData, eventName);
  }

  static readonly instance = new PreferenceService();
}

const PreferenceServiceInstance = PreferenceService.instance;
export default PreferenceServiceInstance;
